import React, { useState } from "react";
import { X, Trash2, GripVertical } from "lucide-react";
import Button, { ButtonType } from "../Assets/Button";
import type { IColumn } from "../../models/LeadData.model";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface ColumnVisibilityModalProps {
  columns: IColumn[];
  visibleColumns: Record<string, boolean>;
  onToggleColumn: (columnId: string) => void;
  onDeleteColumn: (columnId: string) => void;
  onClose: () => void;
  setModalOpen: (isOpen: boolean) => void;
  onColumnOrderChange: (columns: IColumn[]) => void;
}

const protectedColumns = [
  "lead",
  "follow-up",
  "not interested",
  "won",
  "agreed to meet",
];

interface SortableColumnItemProps {
  column: IColumn;
  visibleColumns: Record<string, boolean>;
  onToggleColumn: (columnId: string) => void;
  onDeleteColumn: (columnId: string) => void;
  isProtected: boolean;
  canDelete: boolean;
}

const SortableColumnItem: React.FC<SortableColumnItemProps> = ({
  column,
  visibleColumns,
  onToggleColumn,
  onDeleteColumn,
  isProtected,
  canDelete,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 100 : undefined,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between bg-gray-100 p-2 rounded-lg hover:bg-gray-200 transition"
    >
      <div className="flex items-center gap-2">
        <button
          {...attributes}
          {...listeners}
          className="p-1 text-gray-500 hover:text-gray-700 cursor-grab touch-none"
          aria-label="Drag to reorder"
        >
          <GripVertical size={16} />
        </button>
        <label htmlFor={`checkbox-${column.id}`} className="flex items-center">
          <input
            id={`checkbox-${column.id}`}
            type="checkbox"
            checked={visibleColumns[column.id]}
            onChange={() => onToggleColumn(column.id)}
            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring"
          />
          <span className="text-gray-800 cursor-pointer ml-2">
            {column.name}
          </span>
        </label>
      </div>

      {!isProtected && (
        <button
          onClick={() => onDeleteColumn(column.id)}
          disabled={!canDelete}
          className={`p-2 rounded-lg transition ${
            !canDelete
              ? "text-gray-400 cursor-not-allowed"
              : "text-red-500 hover:text-red-600"
          }`}
          aria-label="delete"
        >
          <Trash2 size={18} />
        </button>
      )}
    </li>
  );
};

const ColumnVisibilityModal: React.FC<ColumnVisibilityModalProps> = ({
  columns,
  setModalOpen,
  visibleColumns,
  onToggleColumn,
  onDeleteColumn,
  onClose,
  onColumnOrderChange,
}) => {
  const [localColumns, setLocalColumns] = useState(columns);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setLocalColumns((columns) => {
        const oldIndex = columns.findIndex((col) => col.id === active.id);
        const newIndex = columns.findIndex((col) => col.id === over?.id);

        const newColumns = arrayMove(columns, oldIndex, newIndex);
        return newColumns;
      });
    }
  };

  const handleDone = () => {
    onColumnOrderChange(localColumns);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center animate-fadeIn z-50 p-4">
      <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-xs sm:max-w-sm md:max-w-md">
        <div className="flex justify-between items-center mb-4 border-b pb-3">
          <h2 className="text-lg font-semibold text-gray-800">
            Column Visibility
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="close"
          >
            <X size={20} />
          </button>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={localColumns.map((col) => col.id)}
            strategy={verticalListSortingStrategy}
          >
            <ul className="space-y-3 text-sm">
              {localColumns.map((column) => {
                const isProtected = protectedColumns.includes(
                  column.name.toLowerCase()
                );
                const canDelete = !isProtected && column.cards.length === 0;

                return (
                  <SortableColumnItem
                    key={column.id}
                    column={column}
                    visibleColumns={visibleColumns}
                    onToggleColumn={onToggleColumn}
                    onDeleteColumn={onDeleteColumn}
                    isProtected={isProtected}
                    canDelete={canDelete}
                  />
                );
              })}
            </ul>
          </SortableContext>
        </DndContext>

        <div className="mt-4 flex justify-end">
          <Button
            type={ButtonType.Submit}
            onClick={handleDone}
            className="w-full text-sm"
          >
            Update
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ColumnVisibilityModal;
